import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import type { PortalForm } from './api';
import { usePortalForms, usePortalSuggestions, useSubmitPortalForm } from './queries';
import { Select } from '../../components/Select';

/**
 * Anfragetypen im Portal -- F4-20, F4-18.
 *
 * **Der Anfragetyp steht vor dem Betreff.** Er bestimmt, welche Felder
 * erscheinen -- und wer zuerst den Betreff schreibt und dann den Typ wechselt,
 * bekommt eine andere Maske unter der Hand.
 *
 * "Ohne Typ" bleibt immer moeglich: Ein Kunde, der nicht weiss, wohin sein
 * Anliegen gehoert, soll es trotzdem loswerden koennen. Genau dafuer gibt es
 * die freie Anfrage in `PortalTicketList`.
 */
export function PortalRequestForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation();
  const forms = usePortalForms();
  const submit = useSubmitPortalForm();

  const [formId, setFormId] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [answers, setAnswers] = useState<Record<string, unknown>>({});

  const form = forms.data?.find((candidate) => candidate.id === formId);

  if (!forms.data || forms.data.length === 0) {
    return null;
  }

  return (
    // Mit Beschriftung: Der Abschnitt hat damit die Rolle "region" und laesst
    // sich von einer Sprachausgabe -- und von einem Test -- als Einheit
    // ansprechen. Betreff und Absendeknopf gibt es auf dieser Seite zweimal.
    <section className="card" aria-label={t('portal.requestType')}>
      <h2>{t('portal.requestType')}</h2>

      <div className="field">
        <label className="field__label" htmlFor="portal-form">
          {t('portal.chooseRequestType')}
        </label>
        <Select
          id="portal-form"
          className="field__control"
          value={formId}
          onChange={(event) => {
            setFormId(event.target.value);
            // Die Antworten gehoeren zum Typ. Sie stehen zu lassen hiesse,
            // Werte an Felder zu haengen, die es im neuen Typ nicht gibt --
            // der Server weist sie ab, und der Melder erfaehrt nicht warum.
            setAnswers({});
          }}
        >
          <option value="">{t('portal.noRequestType')}</option>
          {forms.data.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </Select>
      </div>

      {form ? (
        <>
          {form.intro ? <p className="table__muted">{form.intro}</p> : null}

          <div className="field">
            <label className="field__label" htmlFor="portal-form-subject">
              {t('portal.subject')}
            </label>
            <input
              id="portal-form-subject"
              className="field__control"
              value={subject}
              onChange={(event) => {
                setSubject(event.target.value);
              }}
            />
          </div>

          {/* Loesungsvorschlaege waehrend der Erfassung -- F4-13, K-107.
              Der Katalog verlangt sie ausdruecklich fuer beide Masken: Ein
              Vorschlag, der erst nach dem Absenden kommt, hat den Vorgang
              schon ausgeloest, den er verhindern sollte. */}
          <PortalSuggestions text={subject} />

          {(form.fields ?? []).map((field) => (
            <FieldInput
              key={field.key}
              field={field}
              value={answers[field.key]}
              onChange={(value) => {
                setAnswers((current) => ({ ...current, [field.key]: value }));
              }}
            />
          ))}

          <div className="field">
            <label className="field__label" htmlFor="portal-form-message">
              {t('portal.description')}
            </label>
            <textarea
              id="portal-form-message"
              className="field__control field__control--area"
              rows={4}
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
              }}
            />
          </div>

          {submit.isError ? <ErrorNotice error={submit.error} /> : null}

          <Button
            variant="primary"
            disabled={subject.trim().length < 3}
            busy={submit.isPending}
            onClick={() => {
              submit.mutate(
                { formId, subject: subject.trim(), message, answers },
                {
                  onSuccess: () => {
                    setSubject('');
                    setMessage('');
                    setAnswers({});
                    setFormId('');
                    onCreated();
                  },
                },
              );
            }}
          >
            {t('portal.send')}
          </Button>
        </>
      ) : null}
    </section>
  );
}

type FieldType = NonNullable<PortalForm['fields']>[number];

/** Ein Feld des Anfragetyps -- die sechs Arten aus `webform/fields.py`. */
function FieldInput({
  field,
  value,
  onChange,
}: {
  field: FieldType;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const id = `portal-field-${field.key}`;
  const text = typeof value === 'string' ? value : '';

  if (field.type === 'checkbox') {
    return (
      <label className="field__checkbox">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
        />
        {field.label}
      </label>
    );
  }

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {field.label}
        {field.required ? ' *' : ''}
      </label>

      {field.type === 'textarea' ? (
        <textarea
          id={id}
          className="field__control field__control--area"
          rows={3}
          value={text}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      ) : null}

      {field.type === 'select' ? (
        <Select
          id={id}
          className="field__control"
          value={text}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        >
          <option value="" />
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      ) : null}

      {field.type === 'text' || field.type === 'number' || field.type === 'date' ? (
        <input
          id={id}
          type={field.type === 'text' ? 'text' : field.type}
          className="field__control"
          value={text}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Vorschlaege im Portal -- F4-13, K-107.
 *
 * **Was der Kunde hier sieht, entscheidet der Server.** ``visible_levels``
 * laesst fuer einen Portalnutzer ausschliesslich veroeffentlichte Artikel der
 * Stufe ``portal`` oder ``public`` durch. Diese Datei filtert nichts nach --
 * eine zweite Sichtbarkeitsregel waere eine zweite Wahrheit, und der
 * Unterschied waere ein interner Artikel im Portal.
 *
 * Sie bleibt still, wenn sie nichts hat: Ein "keine Treffer" waere Laerm in
 * einer Maske, die jemand ausfuellt, weil er ein Problem hat.
 */
function PortalSuggestions({ text }: { text: string }) {
  const { t } = useTranslation();
  const suggestions = usePortalSuggestions(text);

  if (!suggestions.data || suggestions.data.length === 0) {
    return null;
  }

  return (
    <section className="card" aria-label={t('portal.suggestions')}>
      <h2>{t('portal.suggestions')}</h2>
      <p className="table__muted">{t('portal.suggestionsHint')}</p>
      <ul className="stack">
        {suggestions.data.map((article) => (
          <li key={article.id}>
            <span className="table__subject">{article.title}</span>
            <div className="table__muted">{article.summary}</div>
          </li>
        ))}
      </ul>
    </section>
  );
}
